import { useContext, useRef, useEffect, useMemo, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Building2,
  Package,
  CreditCard,
  BarChart3,
  FileBarChart,
  Receipt,
  Users,
  CalendarDays,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganization } from "@/hooks/useOrganization";
import { motion } from "framer-motion";
import { useHasRole } from "@/hooks/useHasRole";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/types/featureFlags";
import { LanguageProviderContext } from "@/providers/LanguageProvider";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import FloatingButtonNavigation from "@/Layout/FloatingButtonNavigation";

type DashboardNavLink = {
  path: string;
  icon: LucideIcon;
  label: string;
};

const navItemClassName = (isActive: boolean) =>
  cn(
    "relative z-10 inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex-1 sm:flex-none min-w-[90px] sm:min-w-0",
    isActive
      ? "text-foreground"
      : "text-sidebar-foreground/75 hover:text-sidebar-foreground dark:text-muted-foreground dark:hover:text-foreground",
  );

const DashboardLayout = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { isAdmin, isAdminOrManager, isAccountant, hasAnyRole } = useHasRole();
  const { isEnabled } = useFeatureFlag();
  const { translator } = useContext(LanguageProviderContext);
  const location = useLocation();
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
    top: number;
    height: number;
  }>({
    left: 0,
    width: 0,
    top: 0,
    height: 0,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = useMemo((): DashboardNavLink[] => {
    if (isAccountant()) {
      return [
        { path: "/sales", icon: BarChart3, label: "Home.sales" },
        { path: "/cash", icon: CreditCard, label: "Home.cash" },
      ];
    }

    const showReservations =
      isEnabled(FEATURE_FLAGS.RESERVATIONS) &&
      hasAnyRole(["admin", "manager", "cashier"]);

    return [
      { path: "/tabs", icon: Receipt, label: "Home.tabs" },
      { path: "/products", icon: Package, label: "Home.products" },
      { path: "/clients", icon: Users, label: "Home.clients" },
      ...(showReservations
        ? [{ path: "/reservations", icon: CalendarDays, label: "Home.reservations" }]
        : []),
      ...(isAdminOrManager()
        ? [{ path: "/providers", icon: Building2, label: "Home.providers" }]
        : []),
      { path: "/sales", icon: BarChart3, label: "Home.sales" },
      { path: "/cash", icon: CreditCard, label: "Home.cash" },
      ...(isAdmin()
        ? [{ path: "/logs", icon: FileBarChart, label: "Home.logs" }]
        : []),
    ];
  }, [isAdmin, isAdminOrManager, isAccountant, hasAnyRole, isEnabled]);

  const primaryLinks = useMemo(
    () =>
      navLinks.filter(
        (link) => link.path !== "/providers" && link.path !== "/logs",
      ),
    [navLinks],
  );

  const moreLinks = useMemo(
    () =>
      navLinks.filter(
        (link) => link.path === "/providers" || link.path === "/logs",
      ),
    [navLinks],
  );

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isMoreActive = moreLinks.some((link) => isActive(link.path));

  const updateIndicatorPosition = () => {
    if (!containerRef.current) return;

    const activeItem = containerRef.current.querySelector<HTMLElement>(
      '[data-active="true"]',
    );

    if (activeItem) {
      setIndicatorStyle({
        left: activeItem.offsetLeft,
        width: activeItem.offsetWidth,
        top: activeItem.offsetTop,
        height: activeItem.offsetHeight,
      });
    } else {
      setIndicatorStyle((prev) => ({
        ...prev,
        width: 0,
        height: 0,
      }));
    }
  };

  useEffect(() => {
    updateIndicatorPosition();
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      updateIndicatorPosition();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (location.pathname === "/fast-switch" || location.pathname === "/kitchen") {
    return (
      <div className="min-h-screen bg-background">
        <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </main>
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{translator("Home.welcomeToTabstr")}</CardTitle>
            <CardDescription>
              {translator("Home.createAOrganizationMessage")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button
              type="button"
              variant="default"
              className="w-full sm:w-auto"
              onClick={() => navigate("/fast-switch", { replace: true })}
            >
              {translator("FastSwitch.title")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => navigate("/auth", { replace: true })}
            >
              {translator("Global.goToLogin")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className={cn("w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6", isMobile && "pb-28")}>
        <div className="space-y-6">
          <div
            ref={containerRef}
            className="relative top-3 z-50 hidden sm:grid items-center justify-center sm:justify-start rounded-md bg-sidebar backdrop-blur-sm border border-sidebar-border p-1 text-sidebar-foreground dark:bg-muted/95 dark:border-border/50 dark:text-muted-foreground w-full min-h-10 gap-1"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            }}
          >
            <motion.div
              animate={{
                left: indicatorStyle.left,
                width: indicatorStyle.width,
                top: indicatorStyle.top,
                height: indicatorStyle.height,
              }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 30,
              }}
              className="absolute rounded-sm bg-card shadow-sm border border-border dark:bg-background"
            />

            {primaryLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-active={isActive(link.path)}
                className={navItemClassName(isActive(link.path))}
              >
                <link.icon className="h-4 w-4 mr-2" />
                {translator(link.label)}
              </Link>
            ))}

            {moreLinks.length > 0 && (
              <DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-active={isMoreActive}
                    className={navItemClassName(isMoreActive)}
                    aria-label={translator("Navigation.more")}
                  >
                    {translator("Navigation.more")}
                    <motion.span
                      className="ml-1 inline-flex"
                      animate={{ rotate: moreOpen ? 180 : 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 30,
                      }}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </motion.span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={6}
                  aria-label={translator("Navigation.more")}
                  className="min-w-[var(--radix-dropdown-menu-trigger-width)] rounded-md border border-sidebar-border bg-sidebar p-1 text-sidebar-foreground shadow-sm dark:border-border dark:bg-muted dark:text-muted-foreground"
                >
                  {moreLinks.map((link) => (
                    <DropdownMenuItem key={link.path} asChild>
                      <Link
                        to={link.path}
                        className={cn(
                          "cursor-pointer gap-2 rounded-sm px-3 py-2 text-sm font-medium",
                          isActive(link.path)
                            ? "border border-border bg-card text-foreground shadow-sm dark:bg-background focus:bg-card focus:text-foreground"
                            : "border border-transparent text-sidebar-foreground/75 hover:text-sidebar-foreground focus:bg-transparent focus:text-sidebar-foreground dark:text-muted-foreground dark:hover:text-foreground dark:focus:text-foreground",
                        )}
                      >
                        <link.icon className="h-4 w-4" />
                        {translator(link.label)}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {isMobile && (
            <FloatingButtonNavigation
              navLinks={[...primaryLinks, ...moreLinks]}
              isActive={isActive}
              translator={translator}
            />
          )}

          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
